// Fonction utilitaire pour extraire les données d'une charge
const extractChargeData = (charge) => {
  const rubrique = Array.isArray(charge.rubriques) && charge.rubriques[0]?.rubrique
    ? charge.rubriques[0].rubrique
    : (typeof charge.rubriques === "object" && charge.rubriques !== null && charge.rubriques.rubrique
        ? charge.rubriques.rubrique
        : (typeof charge.rubriques === "string" ? charge.rubriques : "-"));
  
  const sousRubrique = Array.isArray(charge.rubriques) && charge.rubriques[0]?.sousRubrique
    ? charge.rubriques[0].sousRubrique
    : (typeof charge.rubriques === "object" && charge.rubriques !== null && charge.rubriques.sousRubrique
        ? charge.rubriques.sousRubrique
        : "-");
  
  const fournisseur = Array.isArray(charge.rubriques) && charge.rubriques[0]?.fournisseur
    ? charge.rubriques[0].fournisseur
    : charge.fournisseur || "-";
  
  const dateValidation = (charge.dateValidation || charge.dateValidationDG || charge.created)
    ? (() => {
        const dateField = charge.dateValidation || charge.dateValidationDG || charge.created;
        const date = dateField.seconds 
          ? new Date(dateField.seconds * 1000)
          : new Date(dateField);
        return date.toLocaleDateString('fr-FR');
      })()
    : "-";
  
  return {
    rubrique,
    sousRubrique,
    fournisseur,
    description: charge.description || "-",
    montant: Number(charge.montant) || 0,
    dateValidation
  };
};

// Fonction utilitaire pour extraire les données d'une dépense
const extractDepenseData = (depense) => {
  const rubrique = Array.isArray(depense.rubriques) && depense.rubriques[0]?.rubrique
    ? depense.rubriques[0].rubrique
    : (typeof depense.rubriques === "object" && depense.rubriques !== null && depense.rubriques.rubrique
        ? depense.rubriques.rubrique
        : (typeof depense.rubriques === "string" ? depense.rubriques : "-"));

  const sousRubrique = Array.isArray(depense.rubriques) && depense.rubriques[0]?.sousRubrique
    ? depense.rubriques[0].sousRubrique
    : (typeof depense.rubriques === "object" && depense.rubriques !== null && depense.rubriques.sousRubrique
        ? depense.rubriques.sousRubrique
        : "-");

  const fournisseur = Array.isArray(depense.rubriques) && depense.rubriques[0]?.fournisseur
    ? depense.rubriques[0].fournisseur
    : depense.fournisseur || "-";

  const dateValidation = (depense.dateValidation || depense.dateValidationDG || depense.created)
    ? (() => {
        const dateField = depense.dateValidation || depense.dateValidationDG || depense.created;
        const date = dateField.seconds 
          ? new Date(dateField.seconds * 1000)
          : new Date(dateField);
        return date.toLocaleDateString('fr-FR');
      })()
    : "-";

  return {
    rubrique,
    sousRubrique,
    fournisseur,
    description: depense.description || "-",
    montant: Number(depense.montant) || 0,
    dateValidation
  };
};

// Export Excel professionnel avec regroupement par rubrique (Charges de structure)
export const exportToExcelCharges = (projectData) => {
  // Vérification et validation des données d'entrée
  if (!projectData || typeof projectData !== 'object') {
    console.error('❌ Erreur: projectData est invalide ou undefined');
    alert('❌ Erreur: Données du projet invalides pour l\'export Excel');
    return;
  }

  // Destructuration avec valeurs par défaut pour éviter les erreurs
  const { 
    projet = 'Projet sans nom', 
    chefChantier = 'Non défini', 
    charges = [], 
    depenses = [], // Fallback au cas où les données sont dans 'depenses'
    total = 0, 
    filtreDateDebut = new Date(), 
    filtreDateFin = new Date() 
  } = projectData;

  // Utiliser charges ou depenses selon ce qui est disponible
  const dataToExport = charges.length > 0 ? charges : depenses;

  // Vérification que nous avons des données à exporter
  if (!Array.isArray(dataToExport) || dataToExport.length === 0) {
    console.warn('⚠️ Aucune donnée à exporter');
    alert('⚠️ Aucune charge ou dépense à exporter pour ce projet.');
    return;
  }

  console.log('📊 Export Excel démarré:', {
    projet,
    nombreDeDonnees: dataToExport.length,
    typeData: charges.length > 0 ? 'charges' : 'depenses'
  });
  
  // Regrouper les charges par rubrique
  const groupedByRubrique = {};
  dataToExport.forEach((item, index) => {
    try {
      const data = charges.length > 0 ? extractChargeData(item) : extractDepenseData(item);
      const rubrique = data.rubrique;
      
      if (!groupedByRubrique[rubrique]) {
        groupedByRubrique[rubrique] = [];
      }
      groupedByRubrique[rubrique].push(data);
    } catch (error) {
      console.warn(`⚠️ Erreur lors du traitement de l'élément ${index}:`, error);
    }
  });
  
  // Calculer les totaux par rubrique
  const rubriqueTotals = {};
  Object.keys(groupedByRubrique).forEach(rubrique => {
    rubriqueTotals[rubrique] = groupedByRubrique[rubrique].reduce((sum, item) => sum + (item.montant || 0), 0);
  });

  // Recalculer le total si nécessaire
  const calculatedTotal = Object.values(rubriqueTotals).reduce((sum, rubriqueTotal) => sum + rubriqueTotal, 0);
  const finalTotal = total || calculatedTotal;
  
  // Créer le contenu HTML formaté pour Excel
  const htmlContent = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="UTF-8">
      <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
      <!--[if gte mso 9]>
      <xml>
        <x:ExcelWorkbook>
          <x:ExcelWorksheets>
            <x:ExcelWorksheet>
              <x:Name>Charges</x:Name>
              <x:WorksheetOptions>
                <x:DisplayGridlines/>
              </x:WorksheetOptions>
            </x:ExcelWorksheet>
          </x:ExcelWorksheets>
        </x:ExcelWorkbook>
      </xml>
      <![endif]-->
      <style>
        body { font-family: Arial, sans-serif; font-size: 11pt; }
        
        .title {
          font-size: 16pt;
          font-weight: bold;
          color: #2563EB;
          text-align: center;
          background-color: #F3F4F6;
          padding: 10px;
          border: 2px solid #2563EB;
        }
        
        .project-info {
          background-color: #F8FAFC;
          border: 1px solid #E5E7EB;
          padding: 5px;
        }
        
        .info-label {
          font-weight: bold;
          color: #4B5563;
        }
        
        .info-value {
          color: #1F2937;
        }
        
        table {
          border-collapse: collapse;
          width: 100%;
          margin-top: 10px;
        }
        
        .header-row {
          background-color: #2563EB;
          color: white;
          font-weight: bold;
          text-align: center;
          border: 1px solid #1D4ED8;
        }
        
        .rubrique-header {
          background-color: #3B82F6;
          color: white;
          font-weight: bold;
          font-size: 12pt;
          text-align: left;
          border: 2px solid #1D4ED8;
          padding: 8px;
        }
        
        .rubrique-total {
          background-color: #DBEAFE;
          color: #1D4ED8;
          font-weight: bold;
          text-align: right;
          border: 1px solid #3B82F6;
          padding: 6px;
        }
        
        .data-row {
          border: 1px solid #E5E7EB;
        }
        
        .data-row:nth-child(even) {
          background-color: #F9FAFB;
        }
        
        .data-cell {
          padding: 6px;
          border: 1px solid #E5E7EB;
          vertical-align: top;
          text-align: left;
        }
        
        .amount-cell {
          text-align: right;
          font-weight: bold;
          color: #059669;
        }
        
        .date-cell {
          text-align: center;
        }
        
        .total-row {
          background-color: #DCFCE7;
          font-weight: bold;
          border: 3px solid #059669;
        }
        
        .total-cell {
          padding: 10px;
          font-size: 12pt;
          font-weight: bold;
        }
        
        .empty-cell {
          background-color: #F3F4F6;
          border: 1px solid #E5E7EB;
        }
      </style>
    </head>
    <body>
      <!-- Titre principal -->
      <table width="100%">
        <tr>
          <td colspan="6" class="title">ÉTAT DES ${charges.length > 0 ? 'CHARGES DE STRUCTURE' : 'DÉPENSES'} - REGROUPÉ PAR RUBRIQUE</td>
        </tr>
      </table>
      
      <br/>
      
      <!-- Informations du projet -->
      <table width="100%">
        <tr>
          <td width="20%" class="project-info info-label">Projet :</td>
          <td width="30%" class="project-info info-value">${projet}</td>
          <td width="20%" class="project-info info-label">Chef de chantier :</td>
          <td width="30%" class="project-info info-value">${chefChantier}</td>
        </tr>
        <tr>
          <td class="project-info info-label">Période :</td>
          <td class="project-info info-value">${new Date(filtreDateDebut).toLocaleDateString('fr-FR')} - ${new Date(filtreDateFin).toLocaleDateString('fr-FR')}</td>
          <td class="project-info info-label">Date d'extraction :</td>
          <td class="project-info info-value">${new Date().toLocaleDateString('fr-FR')}</td>
        </tr>
        <tr>
          <td class="project-info info-label">Nombre d'éléments :</td>
          <td class="project-info info-value">${dataToExport.length}</td>
          <td class="project-info info-label">Total du projet :</td>
          <td class="project-info info-value">${finalTotal.toLocaleString()} FCFA</td>
        </tr>
        <tr>
          <td class="project-info info-label">Nombre de rubriques :</td>
          <td class="project-info info-value">${Object.keys(groupedByRubrique).length}</td>
          <td class="project-info info-label"></td>
          <td class="project-info info-value"></td>
        </tr>
      </table>
      
      <br/>
      
      <!-- Tableau des données regroupées -->
      <table>
        <!-- En-têtes -->
        <tr class="header-row">
          <td class="data-cell" width="22%">RUBRIQUE</td>
          <td class="data-cell" width="22%">SOUS-RUBRIQUE</td>
          <td class="data-cell" width="22%">FOURNISSEUR</td>
          <td class="data-cell" width="22%">MONTANT (FCFA)</td>
          <td class="data-cell" width="12%">DATE VALIDATION</td>
        </tr>
        
        ${Object.entries(groupedByRubrique).map(([rubrique, itemsRubrique]) => {
          const totalRubrique = rubriqueTotals[rubrique];
          return `
            <!-- En-tête de rubrique -->
            <tr>
              <td class="rubrique-header">
                📂 ${rubrique.toUpperCase()} 
                <span style="float: right; color: #9A3412;">${itemsRubrique.length} élément(s)</span>
              </td>
              <td class="empty-cell"></td>
              <td class="empty-cell"></td>
              <td class="rubrique-header" style="color: #EA580C; font-weight: bold; text-align: right;">
                ${totalRubrique.toLocaleString()} FCFA
              </td>
              <td class="empty-cell"></td>
            </tr>
            
            <!-- Éléments de cette rubrique -->
            ${itemsRubrique.map((data, index) => `
              <tr class="data-row">
                <td class="data-cell empty-cell"></td>
                <td class="data-cell">${data.sousRubrique}</td>
                <td class="data-cell">${data.fournisseur}</td>
                <td class="data-cell amount-cell">${data.montant.toLocaleString()}</td>
                <td class="data-cell date-cell">${data.dateValidation}</td>
              </tr>
            `).join('')}
            
            <!-- Sous-total de la rubrique -->
            <tr>
              <td class="data-cell empty-cell"></td>
              <td colspan="2" class="rubrique-total">SOUS-TOTAL ${rubrique.toUpperCase()}</td>
              <td class="rubrique-total" style="color: #EA580C;">${totalRubrique.toLocaleString()}</td>
              <td class="data-cell empty-cell"></td>
            </tr>
            
            <!-- Ligne vide pour séparation -->
            <tr>
              <td colspan="5" style="height: 5px; border: none;"></td>
            </tr>
          `;
        }).join('')}
        
        <!-- Ligne de total général -->
        <tr class="total-row">
          <td class="total-cell" colspan="3">🎯 TOTAL GÉNÉRAL</td>
          <td class="total-cell amount-cell">${finalTotal.toLocaleString()}</td>
          <td class="total-cell"></td>
        </tr>
      </table>
      
      <br/>
      
      <!-- Résumé par rubrique -->
      <table width="50%">
        <tr style="background-color: #F3F4F6; font-weight: bold;">
          <td colspan="3" style="padding: 8px; text-align: center; border: 1px solid #9CA3AF;">RÉSUMÉ PAR RUBRIQUE</td>
        </tr>
        <tr style="background-color: #E5E7EB; font-weight: bold;">
          <td style="padding: 6px; border: 1px solid #9CA3AF;">RUBRIQUE</td>
          <td style="padding: 6px; border: 1px solid #9CA3AF; text-align: center;">NB ÉLÉMENTS</td>
          <td style="padding: 6px; border: 1px solid #9CA3AF; text-align: center;">MONTANT (FCFA)</td>
        </tr>
        ${Object.entries(rubriqueTotals).map(([rubrique, totalRubrique]) => `
          <tr>
            <td style="padding: 6px; border: 1px solid #D1D5DB;">${rubrique}</td>
            <td style="padding: 6px; border: 1px solid #D1D5DB; text-align: center;">${groupedByRubrique[rubrique].length}</td>
            <td style="padding: 6px; border: 1px solid #D1D5DB; text-align: right; font-weight: bold;">${totalRubrique.toLocaleString()}</td>
          </tr>
        `).join('')}
        <tr style="background-color: #DCFCE7; font-weight: bold;">
          <td style="padding: 8px; border: 2px solid #059669;">TOTAL</td>
          <td style="padding: 8px; border: 2px solid #059669; text-align: center;">${dataToExport.length}</td>
          <td style="padding: 8px; border: 2px solid #059669; text-align: right;">${finalTotal.toLocaleString()}</td>
        </tr>
      </table>
      
      <br/>
      
      <!-- Pied de page -->
      <table width="100%">
        <tr>
          <td style="text-align: center; font-size: 9pt; color: #6B7280; border-top: 1px solid #E5E7EB; padding-top: 10px;">
            Document généré automatiquement le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
  
  try {
    // Créer le blob avec le bon type MIME pour Excel
    const blob = new Blob([htmlContent], { 
      type: 'application/vnd.ms-excel;charset=utf-8' 
    });
    
    // Télécharger le fichier
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const fileName = `${charges.length > 0 ? 'charges' : 'depenses'}_${projet.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xls`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    // Message de succès
    setTimeout(() => {
      alert(`✅ Export Excel réussi avec regroupement par rubrique !

📊 Structure du fichier :
• ${Object.keys(groupedByRubrique).length} rubrique(s) différente(s)
• ${dataToExport.length} élément(s) au total
• Sous-totaux par rubrique
• Résumé récapitulatif en fin de document

💡 Le fichier est organisé de manière professionnelle avec des regroupements clairs.`);
    }, 500);
    
  } catch (error) {
    console.error('❌ Erreur lors de la création du fichier Excel:', error);
    alert('❌ Erreur lors de la génération du fichier Excel. Veuillez réessayer.');
  }
};

// Export PDF direct (téléchargement automatique)
export const exportToPDF = (projectData) => {
  // Vérification des données d'entrée
  if (!projectData || typeof projectData !== 'object') {
    console.error('❌ Erreur: projectData est invalide pour l\'export PDF');
    alert('❌ Erreur: Données du projet invalides pour l\'export PDF');
    return;
  }

  const { 
    projet = 'Projet sans nom', 
    chefChantier = 'Non défini', 
    depenses = [], 
    charges = [],
    total = 0, 
    filtreDateDebut = new Date(), 
    filtreDateFin = new Date() 
  } = projectData;

  // Utiliser depenses ou charges selon ce qui est disponible
  const dataToExport = depenses.length > 0 ? depenses : charges;

  if (!Array.isArray(dataToExport) || dataToExport.length === 0) {
    alert('⚠️ Aucune donnée à exporter en PDF pour ce projet.');
    return;
  }

  try {
    // Créer le contenu HTML optimisé pour PDF
    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${depenses.length > 0 ? 'Dépenses' : 'Charges'} - ${projet}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: Arial, sans-serif; 
      font-size: 12px; 
      line-height: 1.4; 
      color: #333; 
      background: white;
      padding: 20px;
    }
    .header { 
      text-align: center; 
      margin-bottom: 25px; 
      padding-bottom: 15px;
      border-bottom: 2px solid #2563eb;
    }
    .header h1 { 
      color: #2563eb; 
      font-size: 20px;
      margin-bottom: 5px;
    }
    .header p { 
      color: #666; 
      font-size: 14px;
    }
    .project-info { 
      background: #f8fafc; 
      padding: 15px; 
      margin-bottom: 20px; 
      border-radius: 5px;
      border-left: 4px solid #2563eb;
    }
    .project-info h2 { 
      color: #2563eb; 
      font-size: 16px;
      margin-bottom: 10px;
    }
    .info-row { 
      display: flex; 
      justify-content: space-between; 
      margin-bottom: 8px;
      flex-wrap: wrap;
    }
    .info-item { 
      flex: 1; 
      min-width: 200px;
      margin-right: 15px;
    }
    .info-label { 
      font-weight: bold; 
      color: #4b5563; 
      font-size: 11px;
      text-transform: uppercase;
    }
    .info-value { 
      color: #1f2937; 
      font-size: 12px;
    }
    table { 
      width: 100%; 
      border-collapse: collapse; 
      margin-bottom: 15px;
      font-size: 10px;
    }
    th, td { 
      border: 1px solid #d1d5db; 
      padding: 8px 6px; 
      text-align: left;
      vertical-align: top;
    }
    th { 
      background: #2563eb;
      color: white;
      font-weight: bold;
      font-size: 9px;
      text-transform: uppercase;
      text-align: center;
    }
    tr:nth-child(even) { 
      background-color: #f9fafb; 
    }
    .total-row { 
      background: #dcfce7 !important;
      font-weight: bold;
    }
    .amount { 
      text-align: right; 
      font-weight: bold;
    }
    .footer { 
      margin-top: 20px;
      text-align: center;
      font-size: 9px;
      color: #6b7280;
      border-top: 1px solid #e5e7eb;
      padding-top: 10px;
    }
    /* Styles d'impression optimisés */
    @media print {
      body { margin: 0; padding: 15px; }
      .header { page-break-after: avoid; }
      .project-info { page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      thead tr { page-break-after: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>État des ${depenses.length > 0 ? 'Dépenses Validées DG' : 'Charges de Structure'}</h1>
    <p>Extraction des données par projet</p>
  </div>
  
  <div class="project-info">
    <h2>${projet}</h2>
    <div class="info-row">
      <div class="info-item">
        <div class="info-label">Chef de chantier</div>
        <div class="info-value">${chefChantier}</div>
      </div>
      <div class="info-item">
        <div class="info-label">Période</div>
        <div class="info-value">${new Date(filtreDateDebut).toLocaleDateString('fr-FR')} - ${new Date(filtreDateFin).toLocaleDateString('fr-FR')}</div>
      </div>
    </div>
    <div class="info-row">
      <div class="info-item">
        <div class="info-label">Date d'extraction</div>
        <div class="info-value">${new Date().toLocaleDateString('fr-FR')} ${new Date().toLocaleTimeString('fr-FR')}</div>
      </div>
      <div class="info-item">
        <div class="info-label">Nombre d'éléments</div>
        <div class="info-value">${dataToExport.length}</div>
      </div>
    </div>
  </div>
  
  <table>
    <thead>
      <tr>
        <th style="width: 15%;">Rubrique</th>
        <th style="width: 15%;">Sous-rubrique</th>
        <th style="width: 15%;">Fournisseur</th>
        <th style="width: 28%;">Description</th>
        <th style="width: 15%;">Montant (FCFA)</th>
        <th style="width: 12%;">Date validation</th>
      </tr>
    </thead>
    <tbody>
      ${dataToExport.map(item => {
        const data = depenses.length > 0 ? extractDepenseData(item) : extractChargeData(item);
        return `
          <tr>
            <td>${data.rubrique}</td>
            <td>${data.sousRubrique}</td>
            <td>${data.fournisseur}</td>
            <td>${data.description}</td>
            <td class="amount">${data.montant.toLocaleString()}</td>
            <td style="text-align: center;">${data.dateValidation}</td>
          </tr>
        `;
      }).join('')}
      <tr class="total-row">
        <td colspan="4"><strong>TOTAL</strong></td>
        <td class="amount"><strong>${total.toLocaleString()}</strong></td>
        <td></td>
      </tr>
    </tbody>
  </table>
  
  <div class="footer">
    Document généré automatiquement le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}
  </div>
</body>
</html>`;

    // Créer un iframe invisible pour générer le PDF
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    // Écrire le contenu dans l'iframe
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(htmlContent);
    iframeDoc.close();

    // Attendre que le contenu soit chargé puis déclencher l'impression
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        
        // Message de succès
        setTimeout(() => {
          alert(`✅ PDF généré avec succès !

📄 Contenu du document :
• ${dataToExport.length} élément(s)
• Total : ${total.toLocaleString()} FCFA
• Format : ${depenses.length > 0 ? 'Dépenses' : 'Charges'}

💡 Utilisez Ctrl+P ou Cmd+P pour imprimer/sauver en PDF si la boîte de dialogue ne s'ouvre pas automatiquement.`);
          
          // Nettoyer l'iframe après utilisation
          document.body.removeChild(iframe);
        }, 1000);
        
      } catch (printError) {
        console.error('❌ Erreur lors de l\'impression:', printError);
        alert('❌ Erreur lors de la génération du PDF. Veuillez réessayer.');
        document.body.removeChild(iframe);
      }
    }, 500);

    console.log('✅ Export PDF démarré');
    
  } catch (error) {
    console.error('❌ Erreur lors de la génération du PDF:', error);
    alert('❌ Erreur lors de la génération du fichier PDF. Veuillez réessayer.');
  }
};

// Fonction utilitaire pour échapper les caractères XML/SVG
const escapeXml = (unsafe) => {
  if (typeof unsafe !== 'string') return String(unsafe || '');
  return unsafe.replace(/[<>&'"]/g, function (c) {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
};

// Export SVG (avec échappement XML corrigé)
export const exportToSVG = (projectData) => {
  // Vérification des données d'entrée
  if (!projectData || typeof projectData !== 'object') {
    console.error('❌ Erreur: projectData est invalide pour l\'export SVG');
    alert('❌ Erreur: Données du projet invalides pour l\'export SVG');
    return;
  }

  const { 
    projet = 'Projet sans nom', 
    chefChantier = 'Non défini', 
    depenses = [], 
    charges = [],
    total = 0, 
    filtreDateDebut = new Date(), 
    filtreDateFin = new Date() 
  } = projectData;

  // Utiliser depenses ou charges selon ce qui est disponible
  const dataToExport = depenses.length > 0 ? depenses : charges;

  if (!Array.isArray(dataToExport) || dataToExport.length === 0) {
    alert('⚠️ Aucune donnée à exporter en SVG pour ce projet.');
    return;
  }
  
  // Calculer les dimensions nécessaires
  const rowHeight = 30;
  const headerHeight = 150;
  const footerHeight = 60;
  const totalHeight = headerHeight + (dataToExport.length * rowHeight) + footerHeight;
  const width = 1000;
  
  // Créer le contenu SVG avec échappement XML
  const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${width}" height="${totalHeight}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <style><![CDATA[
      .header { font-size: 18px; font-weight: bold; fill: #2563eb; font-family: Arial, sans-serif; }
      .subtitle { font-size: 14px; fill: #4b5563; font-family: Arial, sans-serif; }
      .project-title { font-size: 16px; font-weight: bold; fill: #1f2937; font-family: Arial, sans-serif; }
      .project-info { font-size: 11px; fill: #6b7280; font-family: Arial, sans-serif; }
      .table-header { font-size: 10px; font-weight: bold; fill: white; font-family: Arial, sans-serif; }
      .table-cell { font-size: 9px; fill: #374151; font-family: Arial, sans-serif; }
      .table-cell-amount { font-size: 9px; fill: #374151; font-weight: 600; font-family: Arial, sans-serif; }
      .total-row { font-size: 11px; font-weight: bold; fill: #059669; font-family: Arial, sans-serif; }
      .line { stroke: #d1d5db; stroke-width: 1; }
      .header-bg { fill: #2563eb; }
      .row-bg-even { fill: #f9fafb; }
      .total-bg { fill: #dcfce7; }
    ]]></style>
  </defs>
  
  <!-- Arrière-plan -->
  <rect width="${width}" height="${totalHeight}" fill="white"/>
  
  <!-- En-tête -->
  <text x="${width/2}" y="25" text-anchor="middle" class="header">${escapeXml('État des ' + (depenses.length > 0 ? 'Dépenses Validées DG' : 'Charges de Structure'))}</text>
  <text x="${width/2}" y="45" text-anchor="middle" class="subtitle">Extraction des données par projet</text>
  
  <!-- Informations du projet -->
  <rect x="20" y="60" width="${width-40}" height="70" fill="#f8fafc" stroke="#e5e7eb" stroke-width="1" rx="5"/>
  <text x="35" y="80" class="project-title">${escapeXml(projet)}</text>
  <text x="35" y="100" class="project-info">Chef de chantier: ${escapeXml(chefChantier)}</text>
  <text x="35" y="115" class="project-info">Période: ${escapeXml(new Date(filtreDateDebut).toLocaleDateString('fr-FR'))} - ${escapeXml(new Date(filtreDateFin).toLocaleDateString('fr-FR'))} | Extraction: ${escapeXml(new Date().toLocaleDateString('fr-FR'))} | Éléments: ${dataToExport.length}</text>
  
  <!-- En-têtes de colonnes -->
  <rect x="20" y="140" width="${width-40}" height="25" class="header-bg"/>
  <text x="35" y="157" class="table-header">Rubrique</text>
  <text x="180" y="157" class="table-header">Sous-rubrique</text>
  <text x="320" y="157" class="table-header">Fournisseur</text>
  <text x="460" y="157" class="table-header">Description</text>
  <text x="720" y="157" class="table-header">Montant (FCFA)</text>
  <text x="860" y="157" class="table-header">Date</text>
  
  <!-- Données -->
  ${dataToExport.map((item, index) => {
    const y = 170 + (index * rowHeight);
    const data = depenses.length > 0 ? extractDepenseData(item) : extractChargeData(item);
    const isEven = index % 2 === 0;
    
    // Tronquer et échapper les textes
    const rubriqueText = escapeXml(data.rubrique.substring(0, 18) + (data.rubrique.length > 18 ? '...' : ''));
    const sousRubriqueText = escapeXml(data.sousRubrique.substring(0, 18) + (data.sousRubrique.length > 18 ? '...' : ''));
    const fournisseurText = escapeXml(data.fournisseur.substring(0, 18) + (data.fournisseur.length > 18 ? '...' : ''));
    const descriptionText = escapeXml(data.description.substring(0, 35) + (data.description.length > 35 ? '...' : ''));
    const montantText = escapeXml(data.montant.toLocaleString());
    const dateText = escapeXml(data.dateValidation);
    
    return `
      ${isEven ? `<rect x="20" y="${y-5}" width="${width-40}" height="${rowHeight}" class="row-bg-even"/>` : ''}
      <text x="35" y="${y+10}" class="table-cell">${rubriqueText}</text>
      <text x="180" y="${y+10}" class="table-cell">${sousRubriqueText}</text>
      <text x="320" y="${y+10}" class="table-cell">${fournisseurText}</text>
      <text x="460" y="${y+10}" class="table-cell">${descriptionText}</text>
      <text x="850" y="${y+10}" text-anchor="end" class="table-cell-amount">${montantText}</text>
      <text x="860" y="${y+10}" class="table-cell">${dateText}</text>
    `;
  }).join('')}
  
  <!-- Ligne de total -->
  <rect x="20" y="${totalHeight - 50}" width="${width-40}" height="30" class="total-bg" stroke="#059669" stroke-width="1"/>
  <text x="460" y="${totalHeight - 30}" class="total-row">TOTAL</text>
  <text x="850" y="${totalHeight - 30}" text-anchor="end" class="total-row">${escapeXml(total.toLocaleString())}</text>
  
  <!-- Pied de page -->
  <text x="${width/2}" y="${totalHeight - 10}" text-anchor="middle" class="project-info">Document généré le ${escapeXml(new Date().toLocaleDateString('fr-FR'))} à ${escapeXml(new Date().toLocaleTimeString('fr-FR'))}</text>
</svg>`;
  
  try {
    // Télécharger le fichier SVG
    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const fileName = `${depenses.length > 0 ? 'depenses' : 'charges'}_${projet.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.svg`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    
    console.log('✅ Export SVG réussi:', fileName);
  } catch (error) {
    console.error('❌ Erreur lors de l\'export SVG:', error);
    alert('❌ Erreur lors de la génération du fichier SVG. Veuillez réessayer.');
  }
};

// Fonction générique d'export qui détecte automatiquement le type de données
export const exportToExcel = (projectData) => {
  return exportToExcelCharges(projectData);
};
